import React, { createContext, useEffect, useState } from 'react';
import { useStore } from 'zustand';
import { devtools } from 'zustand/middleware';
import { createStore, type StoreApi } from 'zustand/vanilla';

import { openReplayStateLogger } from '../../../hooks/open-replay';
import type { BaseStepsInterface } from '../data-enrichment/types/dataEnrichmentTypes';
import type { SnowflakeDbValues } from '../snowflake-db-inputs';

export interface SnowflakeDataInterface {
  headers: string[];
  combinedData?: object[];
  mappedColumns?: { [arg0: string]: string }[] | null;
  formData?: SnowflakeDbValues;
  oryId?: string | null;
  clientName?: string | null;
  email?: string | null;
}

export type SnowflakeDataSetter = (
  update:
    | SnowflakeDataInterface
    | ((prevData: SnowflakeDataInterface) => SnowflakeDataInterface)
) => void;

export type SnowflakeMapperStoreState = SnowflakeDataInterface & {
  steps: BaseStepsInterface[];
  activeStep: number;
  setActiveStep: (step: number) => void;
  goToNext: () => void;
  goToPrevious: () => void;
  isCompleteStep: (stepIndex: number) => boolean;
  setData: SnowflakeDataSetter;
};

type SnowflakeMapperStore = SnowflakeMapperStoreState & {
  baseSteps: BaseStepsInterface[];
  setBaseSteps: (steps: BaseStepsInterface[]) => void;
};

const createSnowflakeInitialData = (): SnowflakeDataInterface => ({
  headers: [],
  combinedData: [{}],
  mappedColumns: [],
  oryId: '',
  clientName: '',
  email: '',
  formData: {
    database: '',
    schema: '',
    table: '',
  },
});

const clampStep = (step: number, max: number) =>
  Math.min(Math.max(step, 0), max - 1);

const pickDataFromState = (
  state: SnowflakeMapperStoreState
): SnowflakeDataInterface => ({
  headers: state.headers,
  combinedData: state.combinedData,
  mappedColumns: state.mappedColumns,
  formData: state.formData,
  oryId: state.oryId,
  clientName: state.clientName,
  email: state.email,
});

const createSnowflakeMapperStore = ({
  baseSteps,
}: {
  baseSteps: BaseStepsInterface[];
}) => {
  const initialData = createSnowflakeInitialData();
  return createStore<SnowflakeMapperStore>()(
    devtools(
      openReplayStateLogger(
        (set, get) => ({
          ...initialData,
          baseSteps,
          steps: [...baseSteps],
          activeStep: 0,
          setActiveStep: (step) =>
            set((state) => ({
              activeStep: clampStep(step, state.steps.length),
            })),
          goToNext: () =>
            set((state) => ({
              activeStep: clampStep(state.activeStep + 1, state.steps.length),
            })),
          goToPrevious: () =>
            set((state) => ({
              activeStep: clampStep(state.activeStep - 1, state.steps.length),
            })),
          isCompleteStep: (stepIndex) => stepIndex < get().activeStep,
          setData: (update) =>
            set((state) => {
              const prevData = pickDataFromState(state);
              const nextData =
                typeof update === 'function' ? update(prevData) : update;
              return {
                ...state,
                ...nextData,
              };
            }),
          setBaseSteps: (nextSteps) =>
            set((state) => ({
              baseSteps: nextSteps,
              steps: [...nextSteps],
              activeStep: clampStep(state.activeStep, nextSteps.length),
            })),
        }),
        'snowflake_mapper'
      ),
      { name: 'SnowflakeMapper' }
    )
  );
};

const SnowflakeMapperStoreContext =
  createContext<StoreApi<SnowflakeMapperStore> | null>(null);

export const useSnowflakeMapperStore = <T,>(
  selector: (state: SnowflakeMapperStoreState) => T
) => {
  const store = React.useContext(SnowflakeMapperStoreContext);
  if (!store) {
    throw new Error('SnowflakeMapperProvider is missing');
  }
  return useStore(store, selector);
};

export const SnowflakeMapperProvider = ({
  children,
  baseSteps,
}: {
  children: React.ReactElement;
  baseSteps: BaseStepsInterface[];
}) => {
  const [store] = useState(() => createSnowflakeMapperStore({ baseSteps }));

  useEffect(() => {
    store.getState().setBaseSteps(baseSteps);
  }, [store, baseSteps]);

  return (
    <SnowflakeMapperStoreContext.Provider value={store}>
      {children}
    </SnowflakeMapperStoreContext.Provider>
  );
};
